import { Component, OnInit, Inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { AccessDeniedRedirectService } from './access-denied-redirect.service';
import { OktaSetupService } from './okta-setup.service';

@Component({
  selector: 'app-callback',
  template: '<div style="text-align: center; padding: 50px;"><h2>Logging you in...</h2></div>',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CallbackComponent implements OnInit {
  constructor(
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly accessDeniedRedirectService: AccessDeniedRedirectService,
    private readonly oktaSetupService: OktaSetupService
  ) {}

  ngOnInit(): void {
    // The APP_INITIALIZER (OktaSetupService) may have already handled the login
    // redirect and consumed the PKCE code verifier. If tokens are already present,
    // skip handleRedirect() to avoid the "Could not load PKCE codeVerifier" error.
    // Navigation happens here, after user setup, so the location guards can resolve.
    this.oktaAuth.isAuthenticated().catch(() => false).then(isAuthenticated => {
      if (isAuthenticated) {
        this.navigateAfterLogin();
        return;
      }

      return this.oktaAuth.handleRedirect().then(() => {
        this.navigateAfterLogin();
      }).catch(error => {
        console.error('Error handling login callback:', error);

        if (this.accessDeniedRedirectService.isAccessDeniedFromRoute(this.route)) {
          void this.accessDeniedRedirectService.navigateToAccessDenied();
          return;
        }

        this.router.navigate(['/discover']);
      });
    });
  }

  private navigateAfterLogin(): void {
    // handleLoginRedirect removes Okta's stored original URI, so prefer the path
    // captured during app initialization. getOriginalUri covers a redirect that
    // this component handled itself.
    const originalUri = this.oktaSetupService.consumePostLoginRedirect() || this.oktaAuth.getOriginalUri();

    if (originalUri && originalUri !== '/') {
      this.router.navigateByUrl(originalUri);
    } else {
      this.router.navigate(['/discover']);
    }
  }
}
